// ProfilePage.js
import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useContext,
  useCallback,
} from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import MapPicker from "../components/Registration/MapPicker";
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Divider,
  Button,
  TextField,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Breadcrumbs,
  Link,
  IconButton,
  Card,
  CardContent,
  Chip,
} from "@mui/material";
import {
  PhotoCameraOutlined as PhotoIcon,
  Edit,
  Delete,
  Add,
  Home as HomeIcon,
  Work,
  LocationOn,
  Star,
  StarBorder,
} from "@mui/icons-material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import { AuthContext } from "../context/AuthContext";
import { validatePhoneNumber } from "../utils/validation";
import { colors } from "../utils/theme";

const ProfileSchema = z
  .object({
    firstName: z.string().min(2, "First name must be at least 2 characters"),
    lastName: z.string().min(2, "Last name must be at least 2 characters"),
    gender: z.string().optional(),
    email: z.string().email("Invalid email"),
    mobile: z.string().refine(
      (val) => {
        if (!val) return false;
        return validatePhoneNumber(val).valid;
      },
      (val) => ({
        message: validatePhoneNumber(val || "").error || "Invalid mobile number",
      })
    ),
    password: z.string().optional(),
    confirmPassword: z.string().optional(),
  })
  .refine(
    (data) =>
      !data.password || data.password === data.confirmPassword,
    {
      message: "Passwords do not match",
      path: ["confirmPassword"],
    }
  );

const getPasswordStrength = (password) => {
  let score = 0;
  if (!password) return { score: 0, color: "#d32f2f" };
  if (password.length >= 6) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[\W_]/.test(password)) score++;
  return { score, color: score >= 4 ? "#2e7d32" : "#f9a825" };
};

const countryCodes = [
  { code: "+1", label: "US / CA (+1)" },
  { code: "+7", label: "RU (+7)" },
  { code: "+20", label: "EG (+20)" },
  { code: "+27", label: "ZA (+27)" },
  { code: "+30", label: "GR (+30)" },
  { code: "+31", label: "NL (+31)" },
  { code: "+32", label: "BE (+32)" },
  { code: "+33", label: "FR (+33)" },
  { code: "+34", label: "ES (+34)" },
  { code: "+36", label: "HU (+36)" },
  { code: "+39", label: "IT (+39)" },
  { code: "+40", label: "RO (+40)" },
  { code: "+41", label: "CH (+41)" },
  { code: "+43", label: "AT (+43)" },
  { code: "+44", label: "GB (+44)" },
  { code: "+45", label: "DK (+45)" },
  { code: "+46", label: "SE (+46)" },
  { code: "+47", label: "NO (+47)" },
  { code: "+48", label: "PL (+48)" },
  { code: "+49", label: "DE (+49)" },
  { code: "+51", label: "PE (+51)" },
  { code: "+52", label: "MX (+52)" },
  { code: "+53", label: "CU (+53)" },
  { code: "+54", label: "AR (+54)" },
  { code: "+55", label: "BR (+55)" },
  { code: "+56", label: "CL (+56)" },
  { code: "+57", label: "CO (+57)" },
  { code: "+58", label: "VE (+58)" },
  { code: "+60", label: "MY (+60)" },
  { code: "+61", label: "AU (+61)" },
  { code: "+62", label: "ID (+62)" },
  { code: "+63", label: "PH (+63)" },
  { code: "+64", label: "NZ (+64)" },
  { code: "+65", label: "SG (+65)" },
  { code: "+66", label: "TH (+66)" },
  { code: "+81", label: "JP (+81)" },
  { code: "+82", label: "KR (+82)" },
  { code: "+84", label: "VN (+84)" },
  { code: "+86", label: "CN (+86)" },
  { code: "+90", label: "TR (+90)" },
  { code: "+91", label: "IN (+91)" },
  { code: "+92", label: "PK (+92)" },
  { code: "+93", label: "AF (+93)" },
  { code: "+94", label: "LK (+94)" },
  { code: "+95", label: "MM (+95)" },
  { code: "+98", label: "IR (+98)" },
  { code: "+212", label: "MA (+212)" },
  { code: "+213", label: "DZ (+213)" },
  { code: "+216", label: "TN (+216)" },
  { code: "+218", label: "LY (+218)" },
  { code: "+220", label: "GM (+220)" },
  { code: "+221", label: "SN (+221)" },
  { code: "+222", label: "MR (+222)" },
  { code: "+223", label: "ML (+223)" },
  { code: "+224", label: "GN (+224)" },
  { code: "+225", label: "CI (+225)" },
  { code: "+226", label: "BF (+226)" },
  { code: "+227", label: "NE (+227)" },
  { code: "+228", label: "TG (+228)" },
  { code: "+229", label: "BJ (+229)" },
  { code: "+230", label: "MU (+230)" },
  { code: "+231", label: "LR (+231)" },
  { code: "+232", label: "SL (+232)" },
  { code: "+233", label: "GH (+233)" },
  { code: "+234", label: "NG (+234)" },
  { code: "+235", label: "TD (+235)" },
  { code: "+236", label: "CF (+236)" },
  { code: "+237", label: "CM (+237)" },
  { code: "+238", label: "CV (+238)" },
  { code: "+239", label: "ST (+239)" },
  { code: "+240", label: "GQ (+240)" },
  { code: "+241", label: "GA (+241)" },
  { code: "+242", label: "CG (+242)" },
  { code: "+243", label: "CD (+243)" },
  { code: "+244", label: "AO (+244)" },
  { code: "+245", label: "GW (+245)" },
  { code: "+248", label: "SC (+248)" },
  { code: "+249", label: "SD (+249)" },
  { code: "+250", label: "RW (+250)" },
  { code: "+251", label: "ET (+251)" },
  { code: "+252", label: "SO (+252)" },
  { code: "+253", label: "DJ (+253)" },
  { code: "+254", label: "KE (+254)" },
  { code: "+255", label: "TZ (+255)" },
  { code: "+256", label: "UG (+256)" },
  { code: "+257", label: "BI (+257)" },
  { code: "+258", label: "MZ (+258)" },
  { code: "+260", label: "ZM (+260)" },
  { code: "+261", label: "MG (+261)" },
  { code: "+262", label: "RE (+262)" },
  { code: "+263", label: "ZW (+263)" },
  { code: "+264", label: "NA (+264)" },
  { code: "+265", label: "MW (+265)" },
  { code: "+266", label: "LS (+266)" },
  { code: "+267", label: "BW (+267)" },
  { code: "+268", label: "SZ (+268)" },
  { code: "+269", label: "KM (+269)" },
  { code: "+290", label: "SH (+290)" },
  { code: "+291", label: "ER (+291)" },
  { code: "+297", label: "AW (+297)" },
  { code: "+298", label: "FO (+298)" },
  { code: "+299", label: "GL (+299)" },
  { code: "+350", label: "GI (+350)" },
  { code: "+351", label: "PT (+351)" },
  { code: "+352", label: "LU (+352)" },
  { code: "+353", label: "IE (+353)" },
  { code: "+354", label: "IS (+354)" },
  { code: "+355", label: "AL (+355)" },
  { code: "+356", label: "MT (+356)" },
  { code: "+357", label: "CY (+357)" },
  { code: "+358", label: "FI (+358)" },
  { code: "+359", label: "BG (+359)" },
  { code: "+370", label: "LT (+370)" },
  { code: "+371", label: "LV (+371)" },
  { code: "+372", label: "EE (+372)" },
  { code: "+373", label: "MD (+373)" },
  { code: "+374", label: "AM (+374)" },
  { code: "+375", label: "BY (+375)" },
  { code: "+376", label: "AD (+376)" },
  { code: "+377", label: "MC (+377)" },
  { code: "+378", label: "SM (+378)" },
  { code: "+380", label: "UA (+380)" },
  { code: "+381", label: "RS (+381)" },
  { code: "+382", label: "ME (+382)" },
  { code: "+385", label: "HR (+385)" },
  { code: "+386", label: "SI (+386)" },
  { code: "+387", label: "BA (+387)" },
  { code: "+388", label: "MK (+388)" },
  { code: "+389", label: "MK (+388)" },
  { code: "+420", label: "CZ (+420)" },
  { code: "+421", label: "SK (+421)" },
  { code: "+423", label: "LI (+423)" },
  { code: "+500", label: "FK (+500)" },
  { code: "+501", label: "BZ (+501)" },
  { code: "+502", label: "GT (+502)" },
  { code: "+503", label: "SV (+503)" },
  { code: "+504", label: "HN (+504)" },
  { code: "+505", label: "NI (+505)" },
  { code: "+506", label: "CR (+506)" },
  { code: "+507", label: "PA (+507)" },
  { code: "+509", label: "HT (+509)" },
  { code: "+590", label: "GP (+590)" },
  { code: "+591", label: "BO (+591)" },
  { code: "+592", label: "GY (+592)" },
  { code: "+593", label: "EC (+593)" },
  { code: "+594", label: "GF (+594)" },
  { code: "+595", label: "PY (+595)" },
  { code: "+596", label: "MQ (+596)" },
  { code: "+597", label: "SR (+597)" },
  { code: "+598", label: "UY (+598)" },
  { code: "+599", label: "CW (+599)" },
  { code: "+670", label: "TL (+670)" },
  { code: "+672", label: "NF (+672)" },
  { code: "+673", label: "BN (+673)" },
  { code: "+674", label: "NR (+674)" },
  { code: "+675", label: "PG (+675)" },
  { code: "+676", label: "TO (+676)" },
  { code: "+677", label: "SB (+677)" },
  { code: "+678", label: "VU (+678)" },
  { code: "+679", label: "FJ (+679)" },
  { code: "+680", label: "PW (+680)" },
  { code: "+681", label: "WF (+681)" },
  { code: "+682", label: "CK (+682)" },
  { code: "+683", label: "NU (+683)" },
  { code: "+685", label: "WS (+685)" },
  { code: "+686", label: "KI (+686)" },
  { code: "+687", label: "NC (+687)" },
  { code: "+688", label: "TV (+688)" },
  { code: "+689", label: "PF (+689)" },
  { code: "+690", label: "TK (+690)" },
  { code: "+691", label: "FM (+691)" },
  { code: "+692", label: "MH (+692)" },
  { code: "+850", label: "KP (+850)" },
  { code: "+852", label: "HK (+852)" },
  { code: "+853", label: "MO (+853)" },
  { code: "+855", label: "KH (+855)" },
  { code: "+856", label: "LA (+856)" },
  { code: "+880", label: "BD (+880)" },
  { code: "+886", label: "TW (+886)" },
  { code: "+960", label: "MV (+960)" },
  { code: "+961", label: "LB (+961)" },
  { code: "+962", label: "JO (+962)" },
  { code: "+963", label: "SY (+963)" },
  { code: "+964", label: "IQ (+964)" },
  { code: "+965", label: "KW (+965)" },
  { code: "+966", label: "SA (+966)" },
  { code: "+967", label: "YE (+967)" },
  { code: "+968", label: "OM (+968)" },
  { code: "+970", label: "PS (+970)" },
  { code: "+971", label: "AE (+971)" },
  { code: "+972", label: "IL (+972)" },
  { code: "+973", label: "BH (+973)" },
  { code: "+974", label: "QA (+974)" },
  { code: "+975", label: "BT (+975)" },
  { code: "+976", label: "MN (+976)" },
  { code: "+977", label: "NP (+977)" },
  { code: "+992", label: "TJ (+992)" },
  { code: "+993", label: "TM (+993)" },
  { code: "+994", label: "AZ (+994)" },
  { code: "+995", label: "GE (+995)" },
  { code: "+996", label: "KG (+996)" },
  { code: "+998", label: "UZ (+998)" },
];

const AddressManager = ({ user, showSnack, onAddressesChange }) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addingNew, setAddingNew] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [editForm, setEditForm] = useState({
    addressLine1: "",
    addressLine2: "",
    pincode: "",
    city: "",
    state: "",
    country: "",
    addressType: "Home",
    otherType: "",
    location: { type: "Point", coordinates: [0, 0] },
  });
  const [editErrors, setEditErrors] = useState({});

  const fetchAddresses = useCallback(async () => {
    try {
      const { data } = await api.get(`/users/${user.username}/addresses`);
      setAddresses(data.addresses || []);
    } catch (err) {
      console.error("Failed to fetch addresses:", err);
    } finally {
      setLoading(false);
    }
  }, [user?.username]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  const validateEditForm = () => {
    const errors = {};
    if (!editForm.addressLine1.trim()) errors.addressLine1 = "Address is required";
    if (!editForm.pincode.trim()) errors.pincode = "PIN code is required";
    if (!editForm.city.trim()) errors.city = "City is required";
    if (!editForm.state.trim()) errors.state = "State is required";
    if (!editForm.country.trim()) errors.country = "Country is required";
    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetEditForm = (address = null) => {
    if (address) {
      setEditForm({
        addressLine1: address.addressLine1 || "",
        addressLine2: address.addressLine2 || "",
        pincode: address.pincode || "",
        city: address.city || "",
        state: address.state || "",
        country: address.country || "India",
        addressType: address.addressType || "Home",
        otherType: address.otherType || "",
        location: address.location || { type: "Point", coordinates: [0, 0] },
      });
    } else {
      setEditForm({
        addressLine1: "",
        addressLine2: "",
        pincode: "",
        city: "",
        state: "",
        country: "India",
        addressType: "Home",
        otherType: "",
        location: { type: "Point", coordinates: [0, 0] },
      });
    }
    setEditErrors({});
  };

  const handleEdit = (address) => {
    setEditingId(address._id);
    resetEditForm(address);
    setAddingNew(false);
  };

  const handleAddNew = () => {
    setAddingNew(true);
    setEditingId("new");
    resetEditForm(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setAddingNew(false);
    resetEditForm(null);
  };

  const geocodeAddress = async (address) => {
    try {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      const res = await axios.get(
        "https://maps.googleapis.com/maps/api/geocode/json",
        { params: { address, key: apiKey } }
      );
      if (res.data.results.length > 0) {
        const loc = res.data.results[0].geometry.location;
        return { type: "Point", coordinates: [loc.lng, loc.lat] };
      }
      return null;
    } catch {
      return null;
    }
  };

  const handleSave = async () => {
    if (!validateEditForm()) return;
    setSaving(true);

    try {
      let location = editForm.location;
      if (!location?.coordinates || (location.coordinates[0] === 0 && location.coordinates[1] === 0)) {
        const fullAddress = [editForm.addressLine1, editForm.addressLine2, editForm.city, editForm.state, editForm.pincode, editForm.country].filter(Boolean).join(", ");
        const geo = await geocodeAddress(fullAddress);
        if (geo) location = geo;
      }

      const payload = { ...editForm, location };
      delete payload._id;

      let res;
      if (editingId === "new") {
        res = await api.post(`/users/${user.username}/addresses`, payload);
      } else {
        res = await api.put(`/users/${user.username}/addresses/${editingId}`, payload);
      }

      setAddresses(res.data.addresses);
      if (onAddressesChange) onAddressesChange(res.data.addresses);
      showSnack(editingId === "new" ? "Address added successfully" : "Address updated successfully");
      setEditingId(null);
      setAddingNew(false);
    } catch (err) {
      showSnack(err.response?.data?.message || "Failed to save address", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (addressId) => {
    try {
      const res = await api.delete(`/users/${user.username}/addresses/${addressId}`);
      setAddresses(res.data.addresses);
      if (onAddressesChange) onAddressesChange(res.data.addresses);
      showSnack("Address deleted successfully");
    } catch (err) {
      showSnack(err.response?.data?.message || "Failed to delete address", "error");
    }
    setDeleteConfirm(null);
  };

  const handleSetDefault = async (addressId) => {
    try {
      const res = await api.patch(`/users/${user.username}/addresses/${addressId}/default`);
      setAddresses(res.data.addresses);
      if (onAddressesChange) onAddressesChange(res.data.addresses);
      showSnack("Default address updated");
    } catch (err) {
      showSnack("Failed to set default address", "error");
    }
  };

  const handleFieldChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
    if (editErrors[field]) {
      setEditErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const mapSetValue = useCallback((field, value) => {
    handleFieldChange(field, value);
  }, []);

  const mapInitialLocation = useMemo(() => {
    if (editForm.location?.coordinates) {
      const [lng, lat] = editForm.location.coordinates;
      if (lat && lng && lat !== 0 && lng !== 0) {
        return { latitude: lat, longitude: lng };
      }
    }
    return null;
  }, [editForm.location?.coordinates?.[0], editForm.location?.coordinates?.[1]]);

  const getAddressIcon = (type) => {
    switch (type) {
      case "Work": return <Work sx={{ fontSize: 20, color: colors.primary }} />;
      case "Home": return <HomeIcon sx={{ fontSize: 20, color: colors.secondaryContainer }} />;
      default: return <LocationOn sx={{ fontSize: 20, color: colors.onSurfaceVariant }} />;
    }
  };

  const getAddressSummary = (addr) => {
    return [addr.addressLine1, addr.city, addr.pincode].filter(Boolean).join(", ");
  };

  const fetchPincodeCityState = async (pin) => {
    if (pin.length !== 6) return;
    try {
      const res = await axios.get(`https://api.postalpincode.in/pincode/${pin}`);
      if (res.data[0].Status === "Success") {
        const info = res.data[0].PostOffice[0];
        if (!editForm.city) handleFieldChange("city", info.District);
        if (!editForm.state) handleFieldChange("state", info.State);
      }
    } catch {
      // silent
    }
  };

  const renderAddressForm = () => (
    <Box sx={{ p: 2, bgcolor: colors.surfaceContainerLow, borderRadius: 1, mt: 1 }}>
      <MapPicker
        setValue={mapSetValue}
        initialLocation={mapInitialLocation}
      />
      <Box sx={{ mt: 2 }} />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: "block", color: colors.onSurfaceVariant }}>Address Line 1 *</Typography>
          <TextField
            fullWidth size="small" multiline rows={2}
            value={editForm.addressLine1}
            onChange={(e) => handleFieldChange("addressLine1", e.target.value)}
            error={!!editErrors.addressLine1}
            helperText={editErrors.addressLine1}
            placeholder="Building, street, etc."
          />
        </Box>
        <Box>
          <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: "block", color: colors.onSurfaceVariant }}>Address Line 2</Typography>
          <TextField
            fullWidth size="small"
            value={editForm.addressLine2}
            onChange={(e) => handleFieldChange("addressLine2", e.target.value)}
            placeholder="Additional details (optional)"
          />
        </Box>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: "block", color: colors.onSurfaceVariant }}>City *</Typography>
          <TextField
            fullWidth size="small"
            value={editForm.city}
            onChange={(e) => handleFieldChange("city", e.target.value)}
            error={!!editErrors.city}
            helperText={editErrors.city}
            placeholder="City"
          />
        </Box>
        <Box>
          <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: "block", color: colors.onSurfaceVariant }}>PIN / Postal Code *</Typography>
          <TextField
            fullWidth size="small"
            value={editForm.pincode}
            onChange={(e) => {
              handleFieldChange("pincode", e.target.value);
              fetchPincodeCityState(e.target.value);
            }}
            error={!!editErrors.pincode}
            helperText={editErrors.pincode}
            placeholder="PIN code"
          />
        </Box>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: "block", color: colors.onSurfaceVariant }}>State *</Typography>
          <TextField
            fullWidth size="small"
            value={editForm.state}
            onChange={(e) => handleFieldChange("state", e.target.value)}
            error={!!editErrors.state}
            helperText={editErrors.state}
            placeholder="State / Province"
          />
        </Box>
        <Box>
          <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: "block", color: colors.onSurfaceVariant }}>Country *</Typography>
          <TextField
            fullWidth size="small"
            value={editForm.country}
            onChange={(e) => handleFieldChange("country", e.target.value)}
            error={!!editErrors.country}
            helperText={editErrors.country}
            placeholder="Country"
          />
        </Box>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: "block", color: colors.onSurfaceVariant }}>Address Type</Typography>
          <FormControl fullWidth size="small">
            <Select value={editForm.addressType} onChange={(e) => handleFieldChange("addressType", e.target.value)} sx={{ borderRadius: 1 }}>
              <MenuItem value="Home">Home</MenuItem>
              <MenuItem value="Work">Work</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {editForm.addressType === "Other" && (
          <Box>
            <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: "block", color: colors.onSurfaceVariant }}>Specify Type</Typography>
            <TextField
              fullWidth size="small"
              value={editForm.otherType}
              onChange={(e) => handleFieldChange("otherType", e.target.value)}
              placeholder="e.g., Hostel, Dorm"
            />
          </Box>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 2 }}>
        <Button variant="outlined" onClick={handleCancelEdit} disabled={saving}
          sx={{ borderColor: colors.outlineVariant, color: colors.onSurfaceVariant, textTransform: "none" }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={{ bgcolor: colors.secondaryContainer, color: "#000", textTransform: "none", "&:hover": { bgcolor: "#e68a00" } }}>
          {saving ? <CircularProgress size={20} sx={{ color: "#000" }} /> : (editingId === "new" ? "Add Address" : "Save Changes")}
        </Button>
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} sx={{ color: colors.secondaryContainer }} />
      </Box>
    );
  }

  return (
    <Box>
      {addresses.length === 0 && !addingNew ? (
        <Box sx={{ textAlign: "center", py: 4, color: colors.textMuted }}>
          <LocationOn sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography sx={{ mb: 2 }}>No delivery addresses saved yet</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={handleAddNew}
            sx={{ bgcolor: colors.secondaryContainer, color: "#000", textTransform: "none", borderRadius: "20px", "&:hover": { bgcolor: "#e68a00" } }}>
            Add New Address
          </Button>
        </Box>
      ) : (
        <>
          {addresses.map((addr) => {
            const isEditing = editingId === addr._id;
            const typeLabel = addr.addressType === "Other" && addr.otherType ? addr.otherType : addr.addressType || "Home";
            return (
              <Card key={addr._id} sx={{ mb: 1.5, borderRadius: 1, border: "1px solid", borderColor: addr.isDefault ? colors.secondaryContainer : colors.outlineVariant, boxShadow: addr.isDefault ? "0 0 0 1px #fe9800" : "none" }}>
                <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", p: 1.5 }}>
                    <Box sx={{ mr: 1.5 }}>{getAddressIcon(addr.addressType)}</Box>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 13, color: colors.onSurface }}>{typeLabel}</Typography>
                        {addr.isDefault && (
                          <Chip label="Default" size="small" sx={{ height: 20, fontSize: 10, bgcolor: colors.secondaryContainer, color: "#000", fontWeight: 600 }} />
                        )}
                      </Box>
                      <Typography sx={{ fontSize: 12, color: colors.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {getAddressSummary(addr)}
                      </Typography>
                    </Box>
                    {!isEditing && (
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <IconButton size="small" onClick={() => handleEdit(addr)} sx={{ color: colors.primary }}>
                          <Edit fontSize="small" />
                        </IconButton>
                        {!addr.isDefault && (
                          <>
                            <IconButton size="small" onClick={() => setDeleteConfirm(addr._id)} sx={{ color: colors.error }}>
                              <Delete fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleSetDefault(addr._id)} title="Set as default">
                              <StarBorder fontSize="small" sx={{ color: colors.warningAmber }} />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                  {deleteConfirm === addr._id && (
                    <Box sx={{ px: 1.5, pb: 1.5 }}>
                      <Paper sx={{ p: 1.5, bgcolor: "#fdecea", borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ color: colors.error, display: "block", mb: 1 }}>Delete this address?</Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button size="small" variant="outlined" onClick={() => setDeleteConfirm(null)}
                            sx={{ borderColor: colors.outlineVariant, color: colors.onSurfaceVariant, textTransform: "none", fontSize: 12 }}>
                            Cancel
                          </Button>
                          <Button size="small" variant="contained" onClick={() => handleDelete(addr._id)}
                            sx={{ bgcolor: colors.error, color: "#fff", textTransform: "none", fontSize: 12 }}>
                            Delete
                          </Button>
                        </Box>
                      </Paper>
                    </Box>
                  )}
                  {isEditing && (
                    <Box sx={{ px: 1.5, pb: 1.5 }}>
                      {renderAddressForm()}
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
          {!addingNew && (
            <Button fullWidth variant="outlined" startIcon={<Add />} onClick={handleAddNew}
              sx={{ mt: 1, borderColor: colors.outlineVariant, color: colors.primary, textTransform: "none", borderRadius: 1, py: 1 }}>
              Add New Address
            </Button>
          )}
          {addingNew && (
            <Box sx={{ mt: 1 }}>
              <Card sx={{ borderRadius: 1, border: "1px solid", borderColor: colors.secondaryContainer }}>
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 14, color: colors.onSurface, mb: 1 }}>New Address</Typography>
                  {renderAddressForm()}
                </CardContent>
              </Card>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

function renderProfileContent({
  profilePic, fileInputRef, handleImageUpload, colors, handleSubmit, onSubmit,
  renderModernField, renderModernTextField, register, errors, control,
  needsPassword, password, showPassword, setShowPassword, showConfirmPassword,
  setShowConfirmPassword, passwordStrength, countryCode, setCountryCode,
  countryCodes, isEmail, isSubmitting, modernButtonStyle, user, showSnack,
  updateUser, logout, navigate, handleMobileInput,
}) {
  return (
    <>
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Box sx={{ position: "relative", width: 120, height: 120, mx: "auto", mb: 2 }}>
          <Avatar
            src={profilePic || ""}
            sx={{
              width: "100%",
              height: "100%",
              bgcolor: colors.secondaryContainer,
              fontSize: 48,
              cursor: "pointer",
              boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
              transition: "all 0.2s ease",
              "&:hover": { transform: "scale(1.05)" },
            }}
            onClick={() => fileInputRef.current.click()}
          >
            {!profilePic && "P"}
          </Avatar>
          <Box
            sx={{
              position: "absolute",
              bottom: -2,
              right: -2,
              bgcolor: colors.secondaryContainer,
              borderRadius: "50%",
              p: 0.8,
              cursor: "pointer",
            }}
            onClick={() => fileInputRef.current.click()}
          >
            <PhotoIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Box>
        </Box>
        <input type="file" ref={fileInputRef} accept="image/*" hidden onChange={handleImageUpload} />
        <Typography variant="caption" sx={{ color: colors.textMuted }}>
          Click to upload profile picture
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: colors.onSurface }}>
          Personal Information
        </Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 3 }}>
          {renderModernField("First Name", renderModernTextField({ placeholder: "First name", ...register("firstName"), error: !!errors.firstName, helperText: errors.firstName?.message }), true)}
          {renderModernField("Last Name", renderModernTextField({ placeholder: "Last name", ...register("lastName"), error: !!errors.lastName, helperText: errors.lastName?.message }), true)}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 3 }}>
          {renderModernField("Gender",
            <FormControl fullWidth size="small" sx={{ mb: 0 }}>
              <InputLabel sx={{ color: colors.onSurfaceVariant }}>Select Gender</InputLabel>
              <Controller name="gender" control={control} render={({ field }) => (
                <Select {...field} label="Select Gender" sx={{ borderRadius: 1 }}>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              )} />
            </FormControl>
          )}
          {renderModernField("Mobile",
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <FormControl size="small" sx={{ minWidth: { xs: 72, sm: 130 }, flexShrink: 0 }}>
                <Select value={countryCode} onChange={(e) => setCountryCode(e.target.value)}
                  sx={{ borderRadius: 1, height: 40, "& .MuiSelect-select": { py: 1, px: { xs: 1, sm: 1.5 } } }}>
                  {countryCodes.map((cc) => (
                    <MenuItem key={cc.code} value={cc.code}>{cc.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {renderModernTextField({ placeholder: "Mobile number", inputMode: "numeric", type: "tel", ...register("mobile"), onChange: handleMobileInput, error: !!errors.mobile, helperText: errors.mobile?.message, sx: { flex: 1 } })}
            </Box>, true
          )}
        </Box>

        {renderModernField("Email", renderModernTextField({ placeholder: "your@email.com", type: "email", ...register("email"), error: !!errors.email, helperText: errors.email?.message }), true)}

        {needsPassword && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: colors.onSurface }}>
              Set Security Password
            </Typography>
            {renderModernField("Password",
              <Box>
                {renderModernTextField({ placeholder: "Enter password", type: showPassword ? "text" : "password", ...register("password", { required: "Password is required", minLength: { value: 6, message: "Minimum 6 characters" } }), error: !!errors.password, helperText: errors.password?.message, InputProps: { endAdornment: <Button size="small" onClick={() => setShowPassword(!showPassword)} sx={{ mr: -1 }}>{showPassword ? "Hide" : "Show"}</Button> } })}
                {password && (
                  <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ height: 6, flex: 1, bgcolor: "#e0e0e0", borderRadius: 1, overflow: "hidden" }}>
                      <Box sx={{ height: "100%", width: `${(passwordStrength.score / 5) * 100}%`, bgcolor: passwordStrength.color, transition: "width 0.3s" }} />
                    </Box>
                    <Typography variant="caption" sx={{ color: passwordStrength.color }}>
                      {passwordStrength.score <= 2 ? "Weak" : passwordStrength.score <= 3 ? "Fair" : "Strong"}
                    </Typography>
                  </Box>
                )}
              </Box>, true
            )}
            {renderModernField("Confirm Password", renderModernTextField({ placeholder: "Confirm password", type: showConfirmPassword ? "text" : "password", ...register("confirmPassword", { required: "Please confirm password" }), error: !!errors.confirmPassword, helperText: errors.confirmPassword?.message, InputProps: { endAdornment: <Button size="small" onClick={() => setShowConfirmPassword(!showConfirmPassword)} sx={{ mr: -1 }}>{showConfirmPassword ? "Hide" : "Show"}</Button> } }), true)}
          </>
        )}

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
          <Button type="submit" variant="contained" disabled={isSubmitting}
            sx={{ ...modernButtonStyle, bgcolor: colors.secondaryContainer, color: "#000", flex: 1 }}>
            {isSubmitting ? <CircularProgress size={24} sx={{ color: colors.secondaryContainer }} /> : "Save Profile"}
          </Button>
          <Button variant="outlined" onClick={() => { logout(); navigate("/login"); }}
            sx={{ ...modernButtonStyle, color: colors.secondaryContainer, borderColor: colors.secondaryContainer, flex: 1 }}>
            Logout
          </Button>
        </Box>
      </form>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: colors.onSurface }}>
        Delivery Addresses
      </Typography>

      <AddressManager user={user} showSnack={showSnack} onAddressesChange={(addrs) => {
        if (addrs && user) {
          updateUser({ ...user, addresses: addrs }, true);
        }
      }} />
    </>
  );
}

const ProfilePage = ({ fullWidth }) => {
  const navigate = useNavigate();
  const { user, loading, logout, updateUser } = useContext(AuthContext);
  const fileInputRef = useRef(null);

  const [profilePic, setProfilePic] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [needsPassword, setNeedsPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0 });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [countryCode, setCountryCode] = useState("+91");
  const isEmail = user?.username?.includes("@");

  const handleMobileInput = (e) => {
    const sanitizedValue = e.target.value.replace(/\D/g, "");
    e.target.value = sanitizedValue;
    setValue("mobile", sanitizedValue, { shouldValidate: true, shouldDirty: true });
  };

  const {
    register, control, setValue, handleSubmit, watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(ProfileSchema),
    mode: "onBlur",
    defaultValues: { firstName: "", lastName: "", gender: "", email: "", mobile: "", password: "", confirmPassword: "" },
  });

  const password = watch("password");

  const showSnack = (msg, severity = "success") => setSnack({ open: true, message: msg, severity });

  useEffect(() => {
    setPasswordStrength(getPasswordStrength(password || ""));
  }, [password]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    setNeedsPassword(!user.hasPassword);
    setValue("firstName", user.firstName || "");
    setValue("lastName", user.lastName || "");
    setValue("gender", user.gender || "");
    const username = user.username || "";
    const isEmail = username.includes("@");
    if (user.countryCode) setCountryCode(user.countryCode);
    setValue("mobile", user.mobile || (!isEmail ? username : ""));
    setValue("email", user.email || (isEmail ? username.toLowerCase() : ""));
    if (user.profilePic) setProfilePic(user.profilePic);
    setIsLoading(false);
  }, [user, loading, navigate, setValue]);

  const handleImageUpload = async (e) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;
      const formData = new FormData();
      formData.append("image", file);
      const res = await api.post("/users/profile-picture", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setProfilePic(res.data.profilePic);
      updateUser({ ...user, profilePic: res.data.profilePic }, true);
      showSnack("Profile picture uploaded successfully");
    } catch (err) {
      showSnack(err.response?.data?.message || "Failed to upload image", "error");
    }
  };

  const onSubmit = async (data) => {
    try {
      if (needsPassword) {
        if (!data.password || data.password.length < 6) { showSnack("Password must be at least 6 characters", "error"); return; }
        if (data.password !== data.confirmPassword) { showSnack("Passwords do not match", "error"); return; }
        const passRes = await api.post("/auth/set-password", { username: user.username, password: data.password });
        localStorage.setItem("token", passRes.data.token);
        if (passRes.data.refreshToken) localStorage.setItem("refreshToken", passRes.data.refreshToken);
        updateUser(passRes.data.user, true);
      }
      const payload = { username: user.username, firstName: data.firstName, lastName: data.lastName, gender: data.gender, email: data.email, mobile: data.mobile, countryCode };
      const res = await api.put("/users/profile", payload);
      updateUser(res.data.user, true);
      showSnack("Profile updated successfully");
      setTimeout(() => { navigate("/"); }, 800);
    } catch (err) {
      showSnack(err.response?.data?.message || "Failed to update profile", "error");
    }
  };

  const renderModernField = (label, field, required = false) => (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: colors.onSurfaceVariant }}>
        {label} {required && <span style={{ color: colors.secondaryContainer }}>*</span>}
      </Typography>
      {field}
    </Box>
  );

  const renderModernTextField = (props) => (
    <TextField
      {...props}
      fullWidth
      variant="outlined"
      size="small"
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: 1,
          transition: "all 0.2s ease",
          "&:hover": { "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.secondaryContainer } },
          "&.Mui-focused": { "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.secondaryContainer, borderWidth: 2 } },
        },
        "& .MuiInputLabel-root.Mui-focused": { color: colors.secondaryContainer },
      }}
    />
  );

  const modernButtonStyle = {
    borderRadius: 1,
    textTransform: "none",
    fontWeight: 600,
    px: 3,
    py: 1,
    transition: "all 0.2s ease",
    boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
    "&:hover": { transform: "translateY(-2px)", boxShadow: "0 4px 8px rgba(0,0,0,0.12)" },
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100dvh" }}>
        <CircularProgress sx={{ color: colors.secondaryContainer }} />
      </Box>
    );
  }

  const contentProps = {
    profilePic, fileInputRef, handleImageUpload, colors, handleSubmit, onSubmit,
    renderModernField, renderModernTextField, register, errors, control,
    needsPassword, password, showPassword, setShowPassword, showConfirmPassword,
    setShowConfirmPassword, passwordStrength, countryCode, setCountryCode,
    countryCodes, isEmail, isSubmitting, modernButtonStyle, user, showSnack,
    updateUser, logout, navigate, handleMobileInput,
  };

  if (fullWidth) {
    return (
      <>
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 1, border: "1px solid", borderColor: colors.outlineVariant, boxShadow: "0 2px 4px rgba(0,0,0,0.08)", width: "100%", boxSizing: "border-box", overflowX: "hidden" }}>
          {renderProfileContent(contentProps)}
        </Paper>
        <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}>
          <Alert severity={snack.severity} variant="filled">{snack.message}</Alert>
        </Snackbar>
      </>
    );
  }

  return (
    <Box sx={{ bgcolor: colors.surfaceBright, py: 4, overflow: "auto" }}>
      <Box sx={{ maxWidth: 700, mx: "auto", px: 2 }}>
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 1, border: "1px solid", borderColor: colors.outlineVariant, boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}>
          {renderProfileContent(contentProps)}
        </Paper>
      </Box>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.severity} variant="filled">{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfilePage;